import * as Sentry from '@sentry/react'
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom'

import { SignedIn, SignedOut } from '@/customer/components/auth/auth-guards'
import { AuthStatus } from '@/customer/components/dev/auth-status'
import { ImpersonationBanner } from '@/customer/components/impersonation-banner'
import { ImpersonationOnly } from '@/customer/components/impersonation-only'
import { MemberBookingGuard } from '@/customer/components/member-booking-guard'
import { NotFound } from '@/customer/components/not-found'
import { OnboardedGate } from '@/customer/components/onboarded-gate'
import { PetScope } from '@/customer/components/pet-scope'
import { PetSubscriptionGuard } from '@/customer/components/pet-subscription-guard'
import { PortalLayout } from '@/customer/components/portal-layout'
import {
  ReloadOnNewRelease,
  ReloadOnNewReleaseFallback,
} from '@/customer/components/reload-on-new-release'
import { useGetMe } from '@/customer/hooks/use-get-me'
import { authClient } from '@/customer/lib/auth'
import { resetPostHog, syncPostHogMember } from '@/customer/lib/posthog'
import { queryClient } from '@/customer/lib/query-client'
import { AccountPage } from '@/customer/pages/account/account-page'
import { BillingPage } from '@/customer/pages/account/billing-page'
import { BookAppointmentPage } from '@/customer/pages/account/book-appointment-page'
import { EmailUpdatePage } from '@/customer/pages/account/email-update-page'
import { EmailVerificationPage } from '@/customer/pages/account/email-verification-page'
import { InvoicePage } from '@/customer/pages/account/invoice-page'
import { OrdersPage } from '@/customer/pages/account/orders-page'
import { PhoneUpdatePage } from '@/customer/pages/account/phone-update-page'
import { PhoneVerificationPage } from '@/customer/pages/account/phone-verification-page'
import { ProfilePage } from '@/customer/pages/account/profile-page'
import { ReschedulingAppointmentPage } from '@/customer/pages/account/reschedule-appointment-page'
import { WelcomePage } from '@/customer/pages/account/welcome-page'
import { LandingPage } from '@/customer/pages/landing-page'
import { AddPetPage } from '@/customer/pages/pets/add-pet-page'
import { IntakePage } from '@/customer/pages/pets/intake-page'
import { PaymentFailedPage } from '@/customer/pages/pets/payment-failed-page'
import { PetCheckoutPage } from '@/customer/pages/pets/pet-checkout-page'
import { PetPlanPage } from '@/customer/pages/pets/pet-plan-page'
import { PetRecordsPage } from '@/customer/pages/pets/pet-records-page'
import { PetUpgradePage } from '@/customer/pages/pets/pet-upgrade-page'
import { PetsPage } from '@/customer/pages/pets/pets-page'
import { PublicBookingPage } from '@/customer/pages/public/public-booking-page'
import { PublicCancellationPage } from '@/customer/pages/public/public-cancellation-page'
import { SignInPage } from '@/customer/pages/sign-in/sign-in-page'
import { SubscriptionRenewalPage } from '@/customer/pages/subscription-renewal-page'
import { routes } from '@/customer/routes'

export function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ReloadOnNewReleaseFallback />}>
      <QueryClientProvider client={queryClient}>
        {import.meta.env.DEV ? <AuthStatus /> : <ImpersonationBanner />}
        <BrowserRouter>
          <ReloadOnNewRelease />
          <Routes>
            {/* Fully public routes */}
            <Route path={routes.public.subscriptionRenewal} element={<SubscriptionRenewalPage />} />

            {/* Token-authenticated booking flows */}
            <Route path={routes.public.booking} element={<PublicBookingPage />} />
            <Route path={routes.public.bookingCancel} element={<PublicCancellationPage />} />

            {/* Landing + sign-in: anonymous only. Signed-in visitors
                bounce to `/account`. */}
            <Route
              element={
                <SignedOut fallback={<Navigate to={routes.account.index} replace />}>
                  <Outlet />
                </SignedOut>
              }
            >
              <Route path={routes.root} element={<LandingPage />} />
              <Route path={routes.signIn} element={<SignInPage />} />
            </Route>

            {/* Portal: onboarded members only */}
            <Route
              path="/*"
              element={
                <SignedIn fallback={<Navigate to={routes.signIn} replace />}>
                  <AnalyticsUserSync />
                  <QueryCacheReset />
                  <OnboardedGate>
                    <PortalLayout>
                      <Routes>
                        <Route path={routes.account.index} element={<AccountPage />} />
                        <Route path={routes.account.billing.index} element={<BillingPage />} />
                        <Route path={routes.account.billing.invoice} element={<InvoicePage />} />
                        <Route path={routes.account.orders} element={<OrdersPage />} />
                        <Route path={routes.account.profile} element={<ProfilePage />} />
                        <Route
                          path={routes.account.phone.verify}
                          element={<PhoneVerificationPage />}
                        />
                        <Route path={routes.account.phone.update} element={<PhoneUpdatePage />} />
                        <Route
                          path={routes.account.email.verify}
                          element={<EmailVerificationPage />}
                        />
                        <Route path={routes.account.email.update} element={<EmailUpdatePage />} />
                        <Route
                          path={routes.account.appointments.reschedule}
                          element={<ReschedulingAppointmentPage />}
                        />
                        <Route path={routes.account.pets.index} element={<PetsPage />} />
                        <Route path={routes.account.pets.add} element={<AddPetPage />} />

                        {/* Pet-scoped routes */}
                        <Route element={<PetScope />}>
                          {/* Only inactive/unsubscribed pets can purchase a new subscription */}
                          <Route
                            path={routes.account.pets.plan}
                            element={
                              <PetSubscriptionGuard require={['no_active_subscription']}>
                                <PetPlanPage />
                              </PetSubscriptionGuard>
                            }
                          />

                          {/* We allow incomplete subs to dwell on the checkout page
                          in order to complete 3DS immediately after subscribing */}
                          <Route
                            path={routes.account.pets.checkout}
                            element={
                              <PetSubscriptionGuard
                                require={['no_active_subscription', 'incomplete']}
                                poll
                              >
                                <PetCheckoutPage />
                              </PetSubscriptionGuard>
                            }
                          />

                          {/* Only incomplete subs can see payment recovery page */}
                          <Route
                            path={routes.account.pets.paymentFailed}
                            element={
                              <PetSubscriptionGuard require={['incomplete']} poll>
                                <PaymentFailedPage />
                              </PetSubscriptionGuard>
                            }
                          />

                          <Route path={routes.account.pets.records} element={<PetRecordsPage />} />

                          {/* Upgrade requires a healthy subscription */}
                          <Route
                            path={routes.account.pets.upgrade}
                            element={
                              <PetSubscriptionGuard require={['healthy']}>
                                <PetUpgradePage />
                              </PetSubscriptionGuard>
                            }
                          />

                          {/* Booking requires a healthy subscription and member-level readiness */}
                          <Route
                            path={routes.account.pets.book}
                            element={
                              <PetSubscriptionGuard require={['healthy']}>
                                <MemberBookingGuard>
                                  <BookAppointmentPage />
                                </MemberBookingGuard>
                              </PetSubscriptionGuard>
                            }
                          />

                          {/* Intake chat prototype: staff-impersonated sessions only. No booking guards, since it no longer hands off to booking. */}
                          <Route
                            path={routes.account.pets.intake}
                            element={
                              <ImpersonationOnly>
                                <IntakePage />
                              </ImpersonationOnly>
                            }
                          />

                          {/* Post-subscribe welcome requires a healthy subscription */}
                          <Route
                            path={routes.account.pets.welcome}
                            element={
                              <PetSubscriptionGuard
                                require={['healthy']}
                                loadingMessage="Loading your subscription details…"
                              >
                                <WelcomePage />
                              </PetSubscriptionGuard>
                            }
                          />
                        </Route>

                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </PortalLayout>
                  </OnboardedGate>
                </SignedIn>
              }
            />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  )
}

/**
 * Feeds the signed-in member's identity to Sentry and PostHog.
 *
 * Sits inside `<SignedIn>` so it mounts on sign-in and unmounts at
 * session end - the same signal `QueryCacheReset` below relies on.
 */
function AnalyticsUserSync() {
  const { data } = useGetMe()
  const { data: session } = authClient.useSession()

  const member = data?.member
  const impersonatedBy = session?.session.impersonatedBy

  useEffect(() => {
    Sentry.setUser(
      member
        ? {
            id: member.id,
            email: member.email,
            // Absent on normal sessions, so `has:user.impersonatedBy`
            // isolates staff-driven errors.
            ...(impersonatedBy && { impersonatedBy }),
          }
        : null
    )
    syncPostHogMember(member, impersonatedBy)
  }, [member, impersonatedBy])

  // Reset is deliberately unmount-only rather than part of the effect
  // above. PostHog stitches a visitor's anonymous events to their person
  // on `identify`, and `reset()` severs that link by minting a new
  // distinct id - so resetting on every identity change would destroy
  // the anonymous-to-signup funnel we're adding PostHog to measure.
  useEffect(() => {
    return () => {
      Sentry.setUser(null)
      resetPostHog()
    }
  }, [])

  return null
}

/**
 * Drops every cached query when the portal route tree unmounts, i.e.
 * whenever a session ends. `queryClient` (`lib/query-client.ts`) is a
 * module singleton with a 5-minute `staleTime`, and sign-in is a SPA
 * route - so without this, a second member signing in on the same tab
 * (no page reload) would be served the previous member's `/me`, pets,
 * and appointments from cache while the refetch is in flight.
 *
 * Lives here rather than next to the sign-out button because
 * `<SignedIn>` unmounting is the actual session-end signal - it also
 * covers staff ending an impersonated session
 * (`components/impersonation-banner.tsx`), cookie expiry, and
 * better-auth's cross-tab sign-out broadcast, none of which go through
 * the profile page's click handler.
 */
function QueryCacheReset() {
  const queryClient = useQueryClient()
  useEffect(() => {
    return () => queryClient.clear()
  }, [queryClient])
  return null
}
